import * as React from 'react';
import './style.scss';
import { AlpacaPosition, AlpacaAccount } from '../../reducers/positionSlice';

interface DashboardProps {
    positions: AlpacaPosition[];
    account: AlpacaAccount;
    totalPnL: number;
    winRate: number;
    totalTrades: number;
    activePositions: number;
}

export const DashboardComponent: React.FC<DashboardProps> = ({
    positions,
    account,
    totalPnL,
    winRate,
    totalTrades,
    activePositions,
}) => {
    const getBestPerformer = (): AlpacaPosition | null => {
        if (positions.length === 0) return null;
        return positions.reduce((best, current) => {
            const bestPL = parseFloat(best.unrealized_pl);
            const currentPL = parseFloat(current.unrealized_pl);
            return currentPL > bestPL ? current : best;
        });
    };

    const getWorstPerformer = (): AlpacaPosition | null => {
        if (positions.length === 0) return null;
        return positions.reduce((worst, current) => {
            const worstPL = parseFloat(worst.unrealized_pl);
            const currentPL = parseFloat(current.unrealized_pl);
            return currentPL < worstPL ? current : worst;
        });
    };

    const bestStock = getBestPerformer();
    const worstStock = getWorstPerformer();
    const portfolioValue = account ? parseFloat(account.portfolio_value) : 0;
    const cashAmount = account ? parseFloat(account.cash) : 0;
    const buyingPower = account ? parseFloat(account.buying_power) : 0;

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    const formatPercentage = (value: number) => {
        return (value * 100).toFixed(2) + '%';
    };

    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <h2>Portfolio Dashboard</h2>
            </div>

            <div className="metrics-grid">
                {/* Portfolio Value */}
                <div className="metric-card">
                    <div className="metric-label">Portfolio Value</div>
                    <div className="metric-value">{formatCurrency(portfolioValue)}</div>
                    <div className="metric-subtext">Total account value</div>
                </div>

                {/* Total P&L */}
                <div className="metric-card">
                    <div className="metric-label">Total P&L</div>
                    <div className={`metric-value ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
                        {formatCurrency(totalPnL)}
                    </div>
                    <div className="metric-subtext">Unrealized profit/loss</div>
                </div>

                {/* Return % */}
                <div className="metric-card">
                    <div className="metric-label">Return %</div>
                    <div className={`metric-value ${totalPnL >= 0 ? 'positive' : 'negative'}`}>
                        {formatPercentage((totalPnL / (portfolioValue - totalPnL)) || 0)}
                    </div>
                    <div className="metric-subtext">Percentage return</div>
                </div>

                {/* Win Rate */}
                <div className="metric-card">
                    <div className="metric-label">Win Rate</div>
                    <div className="metric-value">{formatPercentage(winRate)}</div>
                    <div className="metric-subtext">Successful trades</div>
                </div>

                {/* Total Trades */}
                <div className="metric-card">
                    <div className="metric-label">Total Trades</div>
                    <div className="metric-value">{totalTrades}</div>
                    <div className="metric-subtext">Closed trades</div>
                </div>

                {/* Cash Available */}
                <div className="metric-card">
                    <div className="metric-label">Cash Available</div>
                    <div className="metric-value">{formatCurrency(cashAmount)}</div>
                    <div className="metric-subtext">Unrestricted cash</div>
                </div>

                {/* Buying Power */}
                <div className="metric-card">
                    <div className="metric-label">Buying Power</div>
                    <div className="metric-value">{formatCurrency(buyingPower)}</div>
                    <div className="metric-subtext">Available for trading</div>
                </div>

                {/* Active Positions */}
                <div className="metric-card">
                    <div className="metric-label">Active Positions</div>
                    <div className="metric-value">{activePositions}</div>
                    <div className="metric-subtext">Open stocks</div>
                </div>

                {/* Best Performer */}
                <div className="metric-card">
                    <div className="metric-label">Best Performer</div>
                    <div className="metric-value">
                        {bestStock ? bestStock.symbol : 'N/A'}
                    </div>
                    <div className={`metric-subtext ${bestStock && parseFloat(bestStock.unrealized_pl) >= 0 ? 'positive' : 'negative'}`}>
                        {bestStock ? formatCurrency(parseFloat(bestStock.unrealized_pl)) : 'No positions'}
                    </div>
                </div>

                {/* Worst Performer */}
                <div className="metric-card">
                    <div className="metric-label">Worst Performer</div>
                    <div className="metric-value">
                        {worstStock ? worstStock.symbol : 'N/A'}
                    </div>
                    <div className={`metric-subtext ${worstStock && parseFloat(worstStock.unrealized_pl) >= 0 ? 'positive' : 'negative'}`}>
                        {worstStock ? formatCurrency(parseFloat(worstStock.unrealized_pl)) : 'No positions'}
                    </div>
                </div>
            </div>
        </div>
    );
};
